import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Articles from "@/pages/Articles";
import Newsletter from "@/pages/Newsletter";
import NewsletterIssue from "@/pages/NewsletterIssue";
import Pricing from "@/pages/Pricing";
import SubscribeForm from "@/components/SubscribeForm";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return <div className={`toast ${type}`} data-testid="toast-notification">{message}</div>;
};

const SubscribeModal = ({ isOpen, onClose, showToast }) => {
  return (
    <div
      className={`modal-overlay ${isOpen ? '' : 'modal-hidden'}`}
      onClick={onClose}
      data-testid="subscribe-modal"
    >
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h3>Subscribe Free</h3>
        <p>Follow the companies holding crypto. One idea, one letter, every Tuesday.</p>
        <SubscribeForm showToast={showToast} />
      </div>
    </div>
  );
};

const RequestModal = ({ onClose, showToast }) => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`;
    window.open(
      `mailto:research@nilecapital.co?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(body)}`,
      '_blank'
    );
    showToast('Opening your email client — send to complete the request.', 'info');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="request-modal">
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h3>Commission a Report</h3>
        <p>Tell us what you need. We will send you a scope and price estimate before any work begins.</p>
        <form onSubmit={handleSubmit}>
          <input name="name" type="text" placeholder="Your name" value={form.name} onChange={handleChange} required data-testid="request-name" />
          <input name="email" type="email" placeholder="Your email address" value={form.email} onChange={handleChange} required data-testid="request-email" />
          <input name="subject" type="text" placeholder="Project or company to research" value={form.subject} onChange={handleChange} required data-testid="request-subject" />
          <textarea name="message" placeholder="Describe your requirements and timeline..." value={form.message} onChange={handleChange} required data-testid="request-message" />
          <button type="submit" className="modal-btn" data-testid="request-submit">Submit Request</button>
        </form>
      </div>
    </div>
  );
};

function AppContent() {
  const [toasts, setToasts] = useState([]);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <>
      <ScrollToTop />
      <Navbar onSubscribe={() => setShowSubscribe(true)} />

      <div className="toast-container">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
        ))}
      </div>

      {showSubscribe && <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} showToast={showToast} />}
      {showRequest && <RequestModal onClose={() => setShowRequest(false)} showToast={showToast} />}

      <Routes>
        <Route path="/" element={<Home showToast={showToast} onSubscribe={() => setShowSubscribe(true)} />} />
        <Route path="/about" element={<About showToast={showToast} onSubscribe={() => setShowSubscribe(true)} />} />
        <Route path="/articles" element={<Articles showToast={showToast} onSubscribe={() => setShowSubscribe(true)} />} />
        <Route path="/newsletter" element={<Newsletter showToast={showToast} onSubscribe={() => setShowSubscribe(true)} />} />
        <Route path="/newsletter/:slug" element={<NewsletterIssue showToast={showToast} onSubscribe={() => setShowSubscribe(true)} />} />
        <Route path="/pricing" element={<Pricing showToast={showToast} onSubscribe={() => setShowSubscribe(true)} onRequest={() => setShowRequest(true)} />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
